import React, { FC } from 'react';
import { Alert, Button, CircularProgress, Snackbar } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';
import { DeliveryNoteBatchState, describeFailedOrders } from './use-delivery-note-batch';

type DeliveryNoteBatchSnackbarProps = {
    state: DeliveryNoteBatchState;
    onDismiss: () => void;
};

/**
 * DD-3463: the one snackbar a batch action gets for its delivery notes. It waits
 * while the backend compiles them, then offers the single PDF for download.
 */
const DeliveryNoteBatchSnackbar: FC<DeliveryNoteBatchSnackbarProps> = ({
    state,
    onDismiss,
}: DeliveryNoteBatchSnackbarProps) => {
    const { t: translate } = useTranslation(['orders']);

    if (state.status === 'idle') {
        return null;
    }

    const download = (url: string) => {
        window.open(url);
    };

    const content = () => {
        switch (state.status) {
            case 'pending':
                return (
                    <Alert
                        severity="info"
                        variant="filled"
                        icon={<CircularProgress size={20} color="inherit" />}
                        sx={{ minWidth: 320, boxShadow: 4 }}
                        data-test="delivery-note-batch-snackbar.pending"
                    >
                        {state.processedCount > 0
                            ? translate('orders:COMPILING_DELIVERY_NOTES_PROGRESS', {
                                  processed: state.processedCount,
                                  total: state.total,
                              })
                            : translate('orders:COMPILING_DELIVERY_NOTES', { count: state.total })}
                    </Alert>
                );
            case 'timeout':
                return (
                    <Alert severity="warning" variant="filled" onClose={onDismiss} sx={{ minWidth: 320, boxShadow: 4 }}>
                        {translate('orders:COMPILED_DELIVERY_NOTES_TIMEOUT')}
                    </Alert>
                );
            case 'ready': {
                const { event } = state;
                if (!event.deliveryNotesPdfUrl) {
                    return (
                        <Alert
                            severity="error"
                            variant="filled"
                            onClose={onDismiss}
                            sx={{ minWidth: 320, boxShadow: 4 }}
                            data-test="delivery-note-batch-snackbar.failed"
                        >
                            {translate('orders:COMPILED_DELIVERY_NOTES_FAILED')}
                        </Alert>
                    );
                }
                const message = event.failedOrders.length
                    ? translate('orders:COMPILED_DELIVERY_NOTES_PARTIAL', {
                          count: event.failedOrders.length,
                          orderIds: describeFailedOrders(event.failedOrders),
                      })
                    : translate('orders:COMPILED_DELIVERY_NOTES_READY', { count: event.completedCount });
                return (
                    <Alert
                        severity={event.failedOrders.length ? 'warning' : 'success'}
                        variant="filled"
                        onClose={onDismiss}
                        sx={{ minWidth: 320, boxShadow: 4, alignItems: 'center' }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={() => download(event.deliveryNotesPdfUrl as string)}
                                data-test="delivery-note-batch-snackbar.download"
                            >
                                {translate('orders:DOWNLOAD_PDF')}
                            </Button>
                        }
                        data-test="delivery-note-batch-snackbar.ready"
                    >
                        {message}
                    </Alert>
                );
            }
            default:
                return null;
        }
    };

    return (
        <Snackbar open anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            {content() ?? undefined}
        </Snackbar>
    );
};

export default DeliveryNoteBatchSnackbar;
